/// <reference types="resize-observer-browser" />

import {
    Component,
    Host,
    h,
    Event,
    EventEmitter,
    Element,
    Prop,
    State,
    Watch,
} from "@stencil/core";
import { clamp, range } from "../../utils/numbers";

@Component({
    tag: "uic-pagination",
    styleUrl: "pagination.scss",
})
export class Pagination {
    // Space required for ellipsis and active page
    private static readonly RESERVED_SPACE = 3;

    private observer: ResizeObserver;
    private desiredBoundaryCount: number;
    private desiredSiblingCount: number;
    private buttonWidth: number = 77; // largest current button size, only as default

    @Element() host: HTMLUicPaginationElement;

    @State()
    width: number;

    /** The total page count, i.e. number of last page */
    @Prop()
    totalPages: number;

    @Watch("totalPages")
    validateRequiredProps(value) {
        const isBlank = typeof value !== "number" || value <= 0;
        if (isBlank) {
            throw new Error(
                "total-pages is required and has to be greater than 0"
            );
        }
    }

    /** The current page, in the range of 1 - total-pages */
    @Prop({ mutable: true, reflect: true })
    page: number = 1;

    /**
     * The waiting time after input changes (mobile layout and ellipsis inputs)
     * before the page is updated
     */
    @Prop()
    inputDebounceMs: number = 750;

    /** The maximum visible page count (default 7), useful to force narrow layout */
    @Prop()
    maxVisible: number = 7;

    /** Number of always visible pages at the beginning and end.
     * If not set, will be calculated based on maxVisible ((maxVisible - 3) / 4).
     * May overwrite max-visible.
     */
    @Prop()
    boundaryCount: number;

    /** Number of always visible pages before and after the current one.
     * If not set, will be calculated based on maxVisible ((maxVisible - 3) / 4).
     * May overwrite max-visible.
     */
    @Prop()
    siblingCount: number;

    @Watch("maxVisible")
    @Watch("boundaryCount")
    @Watch("siblingCount")
    updateDesiredCounts() {
        const fourthOfAvailableSpace = Math.floor(
            (this.maxVisible - Pagination.RESERVED_SPACE) / 4
        );

        this.desiredBoundaryCount =
            this.boundaryCount != undefined
                ? this.boundaryCount
                : fourthOfAvailableSpace;
        this.desiredSiblingCount =
            this.siblingCount != undefined
                ? this.siblingCount
                : fourthOfAvailableSpace;
    }

    /** Emitted on page changes */
    @Event({ eventName: "uicChange" }) change: EventEmitter<{ page: number }>;

    private debouncedHandleInputChanges: (event: CustomEvent) => void;

    connectedCallback() {
        if (!window.ResizeObserver) {
            return;
        }

        this.observer = new ResizeObserver((entries) => {
            this.updateButtonWidth();

            const entry = entries[0];
            if (entry.contentBoxSize) {
                // Firefox implements `contentBoxSize` as a single content rect, rather than an array
                const contentBoxSize = Array.isArray(entry.contentBoxSize)
                    ? entry.contentBoxSize[0]
                    : entry.contentBoxSize;
                this.width = contentBoxSize.inlineSize;
            } else {
                this.width = entry.contentRect.width;
            }
        });
    }

    componentWillLoad() {
        this.updateDesiredCounts();

        if (!window.ResizeObserver) {
            setTimeout(() => {
                this.updateButtonWidth();
                this.width = this.host.getBoundingClientRect().width;
            });
        } else {
            this.observer.observe(this.host);
        }

        // implement custom debouncing ... can't use debounce() here due to
        // probably changing inputDebounceMs property
        let timer: any;

        this.debouncedHandleInputChanges = (event: CustomEvent) => {
            clearTimeout(timer);
            timer = setTimeout(
                () => this.handleInputChanges(event),
                this.inputDebounceMs
            );
        };
    }

    private updateButtonWidth() {
        const buttonWidth = parseInt(
            getComputedStyle(this.host)
                .getPropertyValue("--uic-stretched-button-width")
                .replace("px", "")
        );

        if (!isNaN(buttonWidth) && buttonWidth > 0) {
            this.buttonWidth = buttonWidth;
        }
    }

    private handleKeydown(event: KeyboardEvent) {
        event.stopPropagation();

        if (event.key === "Enter") {
            // prevent click event on ellipsis button focused after closing
            event.preventDefault();

            const numberInput = (event.target as HTMLInputElement).closest(
                "uic-input-field-number"
            );
            this.closePopover(numberInput);
            this.handleInput(numberInput);
        }
    }

    private handleInputChanges(event: CustomEvent) {
        if (!event.target) {
            return;
        }

        const target = event.target as HTMLUicInputFieldNumberElement;
        this.closePopover(target);
        this.handleInput(target);
    }

    private handlePopoverHidden(event: CustomEvent) {
        if (!event.target) {
            return;
        }

        const numberInput = (event.target as HTMLUicPopoverElement).querySelector(
            "uic-input-field-number"
        );
        this.handleInput(numberInput);
    }

    private handleInput(uicNumberInput: HTMLUicInputFieldNumberElement) {
        if (!uicNumberInput) {
            return;
        }

        if (isNaN(uicNumberInput.value)) {
            return;
        }

        this.moveToPage(uicNumberInput.value);
    }

    private closePopover(popoverChild: HTMLElement) {
        if (!popoverChild) {
            return;
        }

        const closeEvent = new CustomEvent("uicClosePopover", {
            bubbles: true,
        });
        popoverChild.dispatchEvent(closeEvent);
    }

    private moveToPage(p: number) {
        const prev = this.page;
        this.page = clamp(p, 1, this.totalPages);

        if (prev !== this.page) {
            this.change.emit({ page: this.page });
        }
    }

    private renderMobilePageInput() {
        return (
            <uic-input-field-number
                inputmode="numeric"
                clampOnBlur={true}
                defaultValue={1}
                min={1}
                max={this.totalPages}
                unit={this.totalPages.toString()}
                value={this.page}
                onUicInput={(e) => this.debouncedHandleInputChanges(e)}
                onKeyDown={(e) => this.handleKeydown(e)}
            ></uic-input-field-number>
        );
    }

    private renderPageButtons(count: number, startAt: number = 1) {
        return (
            <uic-segmented-control>
                {range(count, startAt).map((p) => (
                    <uic-button
                        variant="content"
                        size="icon-stretched"
                        class="uic-pagination__page-button"
                        selected={p === this.page}
                        onClick={() => this.moveToPage(p)}
                    >
                        {p}
                    </uic-button>
                ))}
            </uic-segmented-control>
        );
    }

    private renderEllipsis(position: "left" | "right") {
        const placement = position === "left" ? "top-start" : "top-end";
        const skidding = position === "left" ? -3 : 3;

        return (
            <uic-popover
                placement={placement}
                skidding={skidding}
                distance={10}
                onUicHide={(e) => this.handlePopoverHidden(e)}
            >
                <uic-icon-button
                    class="uic-pagination__page-button uic-pagination__page-button--ellipsis"
                    icon="uic:ellipsis"
                    slot="trigger"
                ></uic-icon-button>
                <div class="uic-popover-panel">
                    {this.renderMobilePageInput()}
                </div>
            </uic-popover>
        );
    }

    private renderPartialPages(availableSpace) {
        const remainingSpace = availableSpace - Pagination.RESERVED_SPACE;

        const desiredTotal = Math.max(
            1,
            this.desiredBoundaryCount + this.desiredSiblingCount
        );
        const boundaryFraction = this.desiredBoundaryCount / desiredTotal;

        const boundaryCount = Math.min(
            this.desiredBoundaryCount,
            Math.ceil((remainingSpace * boundaryFraction) / 2)
        );
        const siblingCount = Math.min(
            this.desiredSiblingCount,
            Math.floor((remainingSpace - boundaryCount * 2) / 2)
        );

        const boundaryLimit = boundaryCount + siblingCount * 2 + 2;

        if (this.page <= boundaryLimit - siblingCount) {
            return [
                this.renderPageButtons(boundaryLimit, 1),
                this.renderEllipsis("right"),
                this.renderPageButtons(
                    boundaryCount,
                    this.totalPages - boundaryCount + 1
                ),
            ];
        }

        if (this.page > this.totalPages - (boundaryLimit - siblingCount)) {
            return [
                this.renderPageButtons(boundaryCount, 1),
                this.renderEllipsis("left"),
                this.renderPageButtons(
                    boundaryLimit,
                    this.totalPages - boundaryLimit + 1
                ),
            ];
        }

        return [
            this.renderPageButtons(boundaryCount, 1),
            this.renderEllipsis("left"),
            this.renderPageButtons(
                siblingCount * 2 + 1,
                this.page - siblingCount
            ),
            this.renderEllipsis("right"),
            this.renderPageButtons(
                boundaryCount,
                this.totalPages - boundaryCount + 1
            ),
        ];
    }

    private renderPages() {
        const calculatedSpace =
            this.desiredBoundaryCount * 2 +
            this.desiredSiblingCount * 2 +
            Pagination.RESERVED_SPACE;
        const availableSpace = Math.min(
            Math.floor(this.width / this.buttonWidth) - 2,
            Math.max(this.maxVisible, calculatedSpace)
        );

        if (availableSpace < Pagination.RESERVED_SPACE + 2) {
            return this.renderMobilePageInput();
        }

        if (this.totalPages <= availableSpace) {
            return this.renderPageButtons(this.totalPages);
        }

        return this.renderPartialPages(availableSpace);
    }

    render() {
        if (!this.width) return;

        return (
            <Host class="uic-pagination">
                <uic-button
                    disabled={this.page === 1}
                    size="icon"
                    icon="uic:chevron-left"
                    onClick={() => this.moveToPage(this.page - 1)}
                ></uic-button>
                {this.renderPages()}
                <uic-button
                    disabled={this.page === this.totalPages}
                    size="icon"
                    icon="uic:chevron-right"
                    onClick={() => this.moveToPage(this.page + 1)}
                ></uic-button>
            </Host>
        );
    }
}
